import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { myFirebaseAuth } from '../../firebase/config';
import { logoutUser } from './authService';
import { PromiseToPayCreate, PromiseToPayUpdate } from '@dill/dill-shared';

interface GetBuyerPromiseToPaysParams {
  supplierId: string;
  buyerId: string;
}

export const getBuyerPromiseToPays = createAsyncThunk(
  'promiseToPays/getBuyerPromiseToPays',
  async ({ supplierId, buyerId }: GetBuyerPromiseToPaysParams, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/promiseToPays/buyer/${buyerId}/getBuyerPromiseToPays`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSupplierPromiseToPays = createAsyncThunk(
  'promiseToPays/getSupplierPromiseToPays',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/promiseToPays/getSupplierPromiseToPays`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createPromiseToPay = createAsyncThunk(
  'promiseToPays/createPromiseToPay',
  async ({ supplierId, data }: { supplierId: string; data: PromiseToPayCreate }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/promiseToPays/createPromiseToPay`,
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updatePromiseToPay = createAsyncThunk(
  'promiseToPays/updatePromiseToPay',
  async (
    {
      supplierId,
      promiseToPayId,
      data,
    }: {
      supplierId: string;
      promiseToPayId: string;
      data: PromiseToPayUpdate;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/promiseToPays/${promiseToPayId}/updatePromiseToPay`,
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const cancelPromiseToPay = createAsyncThunk(
  'promiseToPays/cancelPromiseToPay',
  async (
    { supplierId, promiseToPayId }: { supplierId: string; promiseToPayId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/promiseToPays/${promiseToPayId}/cancelPromiseToPay`,
        {},
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getActivePromisesForInvoices = createAsyncThunk(
  'promiseToPays/getActivePromisesForInvoices',
  async ({ supplierId, invoiceIds }: { supplierId: string; invoiceIds: string[] }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const params = new URLSearchParams();
      params.append('invoiceIds', invoiceIds.join(','));

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/promiseToPays/getActivePromisesForInvoices?${params.toString()}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const deletePromiseToPay = createAsyncThunk(
  'promiseToPays/deletePromiseToPay',
  async (
    { supplierId, promiseToPayId }: { supplierId: string; promiseToPayId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/promiseToPays/${promiseToPayId}/deletePromiseToPay`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
